import React, { useState } from 'react';
import {
  BaseEdge,
  SmoothStepEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
} from 'reactflow';

const CORNER_RADIUS = 10;
const STALE_TOLERANCE = 300;

const roundedPolyline = (points, radius) => {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0][0]},${points[0][1]} L ${points[1][0]},${points[1][1]}`;
  }

  let d = `M ${points[0][0]},${points[0][1]}`;

  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i - 1];
    const [cx, cy] = points[i];
    const [nx, ny] = points[i + 1];

    const inLen = Math.hypot(cx - px, cy - py);
    const outLen = Math.hypot(nx - cx, ny - cy);
    const r = Math.min(radius, inLen / 2, outLen / 2);

    if (!Number.isFinite(r) || r < 1) {
      d += ` L ${cx},${cy}`;
      continue;
    }

    const inX = cx - ((cx - px) / inLen) * r;
    const inY = cy - ((cy - py) / inLen) * r;
    const outX = cx + ((nx - cx) / outLen) * r;
    const outY = cy + ((ny - cy) / outLen) * r;

    d += ` L ${inX},${inY} Q ${cx},${cy} ${outX},${outY}`;
  }

  const last = points[points.length - 1];
  d += ` L ${last[0]},${last[1]}`;

  return d;
};

const isPathUsable = (path, sourceX, sourceY, targetX, targetY) => {
  if (!Array.isArray(path) || path.length < 2) return false;

  const first = path[0];
  const last = path[path.length - 1];
  if (!Array.isArray(first) || !Array.isArray(last)) return false;
  if (!Number.isFinite(first[0]) || !Number.isFinite(last[0])) return false;

  return (
    Math.hypot(first[0] - sourceX, first[1] - sourceY) <= STALE_TOLERANCE &&
    Math.hypot(last[0] - targetX, last[1] - targetY) <= STALE_TOLERANCE
  );
};

// ELK ancla la ruta donde reparte los puertos sobre el borde del nodo, que no
// coincide con el handle real. Movemos los extremos al handle y agregamos el
// tramo perpendicular que haga falta para no perder la ortogonalidad.
// El tramo de union sale en la direccion del handle: los handles laterales
// arrancan horizontal y los de arriba/abajo vertical. Si no, todas las salidas
// de un mismo nodo compartirian la misma linea y volverian a encimarse.
const isHorizontalSide = (position) => position === 'left' || position === 'right';

const stitchEnds = (path, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition) => {
  const pts = path.map((p) => [p[0], p[1]]);

  if (pts[0][0] !== sourceX || pts[0][1] !== sourceY) {
    const next = pts[1];
    pts[0] = [sourceX, sourceY];
    if (next && Math.abs(next[0] - sourceX) > 1 && Math.abs(next[1] - sourceY) > 1) {
      pts.splice(1, 0, isHorizontalSide(sourcePosition)
        ? [next[0], sourceY]
        : [sourceX, next[1]]);
    }
  }

  const lastIndex = pts.length - 1;
  if (pts[lastIndex][0] !== targetX || pts[lastIndex][1] !== targetY) {
    const prev = pts[lastIndex - 1];
    pts[lastIndex] = [targetX, targetY];
    if (prev && Math.abs(prev[0] - targetX) > 1 && Math.abs(prev[1] - targetY) > 1) {
      pts.splice(lastIndex, 0, isHorizontalSide(targetPosition)
        ? [prev[0], targetY]
        : [targetX, prev[1]]);
    }
  }

  return pts;
};

const midpointOf = (points) => {
  let total = 0;
  const lengths = [];
  for (let i = 0; i < points.length - 1; i++) {
    const len = Math.hypot(points[i + 1][0] - points[i][0], points[i + 1][1] - points[i][1]);
    lengths.push(len);
    total += len;
  }
  if (total === 0) return [points[0][0], points[0][1]];

  let walked = 0;
  for (let i = 0; i < lengths.length; i++) {
    if (walked + lengths[i] >= total / 2) {
      const t = lengths[i] === 0 ? 0 : (total / 2 - walked) / lengths[i];
      return [
        points[i][0] + (points[i + 1][0] - points[i][0]) * t,
        points[i][1] + (points[i + 1][1] - points[i][1]) * t,
      ];
    }
    walked += lengths[i];
  }
  const last = points[points.length - 1];
  return [last[0], last[1]];
};

const ElkStepEdge = (props) => {
  const {
    id,
    data,
    style,
    markerEnd,
    label,
    labelStyle,
    labelBgStyle,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    selected,
  } = props;

  const [hovered, setHovered] = useState(false);

  const usable = isPathUsable(data?.path, sourceX, sourceY, targetX, targetY);

  let d;
  let labelX;
  let labelY;

  if (usable) {
    const stitched = stitchEnds(
      data.path,
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition
    );
    d = roundedPolyline(stitched, CORNER_RADIUS);
    [labelX, labelY] = midpointOf(stitched);
  } else {
    const [fallbackPath, fx, fy] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
    });
    d = fallbackPath;
    labelX = fx;
    labelY = fy;
  }

  const showLabel = label !== undefined && label !== null && label !== '';
  const highlighted = hovered || selected;

  return (
    <>
      <g onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <BaseEdge
          id={id}
          path={d}
          style={{
            ...style,
            strokeWidth: hovered || selected ? 3 : style?.strokeWidth ?? 2,
          }}
          markerEnd={markerEnd}
          interactionWidth={20}
        />
      </g>
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              fontSize: highlighted ? 10 : labelStyle?.fontSize ?? 8,
              fontWeight: labelStyle?.fontWeight ?? 700,
              color: labelStyle?.fill ?? '#ffffff',
              backgroundColor: labelBgStyle?.fill ?? '#00A884',
              padding: highlighted ? '3px 7px' : '2px 5px',
              borderRadius: '3px',
              whiteSpace: 'nowrap',
              opacity: style?.opacity ?? 1,
              boxShadow: highlighted ? '0 2px 6px rgba(0,0,0,0.3)' : 'none',
              transition: 'font-size 0.15s ease, padding 0.15s ease',
              zIndex: highlighted ? 1002 : 1001,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default React.memo(ElkStepEdge);
